/**
 * Football IQ reward rules (Implementation Guide §26).
 *
 * All pure functions. No React, no Firebase, so the rules that decide
 * how much a user earns are unit-testable and can't be gamed by a stray
 * click handler.
 *
 * The hard rule from PRD §26: opening the same explanation repeatedly must
 * not generate unlimited XP. Every award here is keyed on something that
 * can only happen once.
 */

export const XP = Object.freeze({
  CONCEPT_LEARNED: 10,
  QUIZ_CORRECT: 15,
  QUIZ_INCORRECT: 3, // a wrong answer still taught you something
  PREDICTION_CORRECT: 20,
  GAME_WATCHED: 25,
});

export const LEARNING_SOURCES = Object.freeze({
  EXPLANATION: "explanation", // tapped "Explain this" during a game
  LEARN_TAB: "learn", // read it in the Learn section
  QUIZ: "quiz", // got it right in a quiz
});

/**
 * Award XP for learning a concept, but only the first time.
 *
 * @param {Object.<string, object>} progress conceptId → learning record
 * @param {string} conceptId
 * @param {{source?: string, now?: () => number}} [options]
 * @returns {{
 *   progress: object,      // next progress map (unchanged if already learned)
 *   record: object|null,   // the record to persist, null when nothing changed
 *   xpAwarded: number,
 *   alreadyLearned: boolean
 * }}
 */
export function awardLearningXP(progress = {}, conceptId, options = {}) {
  const { source = LEARNING_SOURCES.EXPLANATION, now = Date.now } = options;

  if (!conceptId) {
    return { progress, record: null, xpAwarded: 0, alreadyLearned: false };
  }

  const existing = progress[conceptId];
  if (existing) {
    // Track how often it was revisited, but never pay for it again.
    const record = {
      ...existing,
      viewCount: Number(existing.viewCount ?? 1) + 1,
      lastViewedAt: now(),
    };
    return {
      progress: { ...progress, [conceptId]: record },
      record,
      xpAwarded: 0,
      alreadyLearned: true,
    };
  }

  const timestamp = now();
  const record = {
    conceptId,
    source,
    learnedAt: timestamp,
    lastViewedAt: timestamp,
    viewCount: 1,
    xpAwarded: XP.CONCEPT_LEARNED,
  };

  return {
    progress: { ...progress, [conceptId]: record },
    record,
    xpAwarded: XP.CONCEPT_LEARNED,
    alreadyLearned: false,
  };
}

/**
 * Score one quiz answer.
 *
 * @param {{id: string, correctOptionId: string, conceptId?: string}} question
 * @param {string} answerId
 * @param {{alreadyAnswered?: boolean}} [options] a re-answer earns nothing
 */
export function scoreQuizAnswer(question, answerId, options = {}) {
  if (!question) {
    return { correct: false, xpAwarded: 0, correctOptionId: null };
  }

  const correct = question.correctOptionId === answerId;
  const alreadyAnswered = Boolean(options.alreadyAnswered);

  let xpAwarded = 0;
  if (!alreadyAnswered) {
    xpAwarded = correct ? XP.QUIZ_CORRECT : XP.QUIZ_INCORRECT;
  }

  return {
    correct,
    xpAwarded,
    correctOptionId: question.correctOptionId,
    conceptId: question.conceptId ?? null,
  };
}

/* ------------------------------------------------ predictions */

export const TOTAL_POINTS_RANGES = Object.freeze([
  { id: "under-20", label: "Under 20", min: 0, max: 19 },
  { id: "20-29", label: "20–29", min: 20, max: 29 },
  { id: "30-39", label: "30–39", min: 30, max: 39 },
  { id: "40-plus", label: "40 or more", min: 40, max: Infinity },
]);

export function rangeForTotalPoints(total) {
  const value = Number(total ?? 0);
  return (
    TOTAL_POINTS_RANGES.find((range) => value >= range.min && value <= range.max) ??
    null
  );
}

/**
 * Score a completed prediction card against the final game state.
 *
 * @param {{
 *   winner?: string,
 *   firstScoringTeam?: string,
 *   turnoverPrediction?: "yes"|"no",
 *   totalPointsRange?: string
 * }} prediction
 * @param {{
 *   winnerTeamId: string|null,
 *   firstScoringTeamId: string|null,
 *   hadTurnover: boolean,
 *   totalPoints: number
 * }} outcome
 */
export function scorePrediction(prediction, outcome) {
  if (!prediction || !outcome) {
    return { results: [], correctCount: 0, xpAwarded: 0 };
  }

  const actualRange = rangeForTotalPoints(outcome.totalPoints);

  const results = [
    {
      key: "winner",
      question: "Who wins?",
      picked: prediction.winner ?? null,
      actual: outcome.winnerTeamId ?? null,
      // A tie means nobody called it right, which is the honest outcome.
      correct:
        Boolean(prediction.winner) &&
        Boolean(outcome.winnerTeamId) &&
        prediction.winner === outcome.winnerTeamId,
    },
    {
      key: "firstScoringTeam",
      question: "Who scores first?",
      picked: prediction.firstScoringTeam ?? null,
      actual: outcome.firstScoringTeamId ?? null,
      correct:
        Boolean(prediction.firstScoringTeam) &&
        Boolean(outcome.firstScoringTeamId) &&
        prediction.firstScoringTeam === outcome.firstScoringTeamId,
    },
    {
      key: "turnoverPrediction",
      question: "Will there be a turnover?",
      picked: prediction.turnoverPrediction ?? null,
      actual: outcome.hadTurnover ? "yes" : "no",
      correct:
        Boolean(prediction.turnoverPrediction) &&
        prediction.turnoverPrediction === (outcome.hadTurnover ? "yes" : "no"),
    },
    {
      key: "totalPointsRange",
      question: "Total points?",
      picked: prediction.totalPointsRange ?? null,
      actual: actualRange?.id ?? null,
      correct:
        Boolean(prediction.totalPointsRange) &&
        prediction.totalPointsRange === actualRange?.id,
    },
  ];

  const correctCount = results.filter((r) => r.correct).length;

  return {
    results,
    correctCount,
    xpAwarded: correctCount * XP.PREDICTION_CORRECT,
  };
}

/** XP for finishing a game, awarded once per game per user. */
export function awardGameWatchedXP(watchedGameIds = [], gameId) {
  if (!gameId || watchedGameIds.includes(gameId)) {
    return { xpAwarded: 0, alreadyWatched: true };
  }
  return { xpAwarded: XP.GAME_WATCHED, alreadyWatched: false };
}
